import { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { consumeSSOLoginPending, hasShownSSOWelcome, markSSOWelcomeShown } from "../../auth/ssoLinkTracking";
import { ROLE_LABEL } from "../../auth/roleView";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import "../../components/Form.css";

/**
 * ADR-0037 §UX's one-time "Link your identity" interstitial, shown on the
 * post-callback landing for a freshly-linked SSO user. See
 * `auth/ssoLinkTracking.ts` for exactly how "freshly-linked" is approximated
 * client-side (a real server-provided signal is the ideal fix, flagged in
 * this PR's notes) and why it is only ever shown once per (browser, user).
 *
 * Deliberately generic about WHICH identity provider and WHICH organization:
 * `sso_config:view` (the only place issuer info lives) is admin-only, and
 * there is no tenant/org display-name concept in this build (ADR-0035 M1
 * PR-D removed the fleet vocabulary) — so the copy carries only what every
 * signed-in session already has, the role, rather than guessing at fields no
 * endpoint returns to a non-admin.
 *
 * Mount this ONCE, above routing, alongside AppShell — matching how
 * `useContainerEventsSubscription` is mounted exactly once (see
 * `app/AppShell.tsx`'s doc comment) so this never re-triggers on ordinary
 * in-app navigation.
 */
export function FirstLoginInterstitial() {
  const { session } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!session) return;
    if (!consumeSSOLoginPending()) return;
    if (hasShownSSOWelcome(session.userId)) return;
    markSSOWelcomeShown(session.userId);
    setOpen(true);
    // Runs once per mount of the authenticated shell (see doc comment) — not
    // meant to react to `session` changing afterward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!session) return null;

  return (
    <Modal open={open} title="You're linked" onClose={() => setOpen(false)}>
      <p>
        Your single sign-on identity is now linked to this Comebye account, as{" "}
        <strong>{ROLE_LABEL[session.role as keyof typeof ROLE_LABEL] ?? session.role}</strong>. Next time, sign in with
        SSO and you'll land straight here.
      </p>
      <div className="form-actions">
        <Button type="button" variant="primary" onClick={() => setOpen(false)}>
          Got it
        </Button>
      </div>
    </Modal>
  );
}
